<template>
  <div class="auth-aside">
    <div class="auth-aside__glow auth-aside__glow--a"></div>
    <div class="auth-aside__glow auth-aside__glow--b"></div>
    <div class="auth-aside__glow auth-aside__glow--c"></div>
    <div class="auth-aside__glow auth-aside__glow--d"></div>
    <div class="auth-aside__noise"></div>
    <div class="auth-aside__body">
      <div class="auth-aside__icon">
        <img src="/static/pop_logo_white.png" alt="" />
      </div>
      <div class="auth-aside__quote-mark">&ldquo;</div>
      <transition name="auth-aside-fade" mode="out-in">
        <p :key="index" class="auth-aside__quote">{{ current.quote }}</p>
      </transition>
      <div class="auth-aside__author">
        <div class="auth-aside__avatar">{{ current.initials }}</div>
        <div>
          <div class="auth-aside__name">{{ current.name }}</div>
          <div class="auth-aside__role">{{ current.role }}</div>
        </div>
      </div>
      <div class="auth-aside__dots">
        <button
          v-for="(item, i) in testimonials"
          :key="item.name"
          type="button"
          class="auth-aside__dot"
          :class="{ 'is-active': i === index }"
          :aria-label="`Show testimonial ${i + 1}`"
          @click="select(i)"
        ></button>
      </div>
      <div class="auth-aside__chips">
        <span v-for="chip in chips" :key="chip" class="auth-aside__chip">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22DC7C" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
          {{ chip }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { TESTIMONIALS, ASIDE_CHIPS } from './testimonials'

const ROTATE_MS = 7000

export default {
  name: 'AuthAside',
  data() {
    return {
      testimonials: TESTIMONIALS,
      chips: ASIDE_CHIPS,
      index: 0,
      timer: null,
    }
  },
  computed: {
    current() {
      return this.testimonials[this.index] || this.testimonials[0]
    },
  },
  mounted() {
    this.start()
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    start() {
      clearInterval(this.timer)
      this.timer = setInterval(() => {
        this.index = (this.index + 1) % this.testimonials.length
      }, ROTATE_MS)
    },
    select(i) {
      this.index = i
      this.start()
    },
  },
}
</script>

<style scoped lang="scss">
@import './auth-tokens.scss';

.auth-aside {
  position: static; // the glows and the noise spread over the whole panel (.pop-auth__aside is the containing block)
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &__glow {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;

    &--a { top: -30%; left: -20%; width: 460px; height: 460px; background: #0e8a99; opacity: 0.75; filter: blur(80px); }
    &--b { top: 30%; right: -30%; width: 420px; height: 420px; background: $pop-accent; opacity: 0.55; filter: blur(90px); }
    &--c { bottom: -35%; left: 10%; width: 380px; height: 380px; background: #ffcc00; opacity: 0.35; filter: blur(90px); }
    &--d { bottom: -10%; right: 5%; width: 300px; height: 300px; background: $pop-success-bright; opacity: 0.3; filter: blur(80px); }
  }

  // film grain over the gradient, as in the prototype (SVG turbulence, overlay blend)
  &__noise {
    position: absolute;
    inset: -50%;
    opacity: 0.35;
    mix-blend-mode: overlay;
    pointer-events: none;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 .6 0'/></filter><rect width='160' height='160' filter='url(%23n)'/></svg>");
  }

  &__body {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  &__icon {
    width: clamp(48px, 7vh, 64px);
    height: clamp(48px, 7vh, 64px);
    border-radius: 18px;
    background: linear-gradient(135deg, #ff7a2e 0%, $pop-accent 100%);
    box-shadow: 0 12px 30px -10px rgba(255, 87, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: clamp(14px, 3.3vh, 30px);

    img { width: 56%; }
  }

  &__quote-mark {
    font-family: 'Mulish', sans-serif;
    font-weight: 800;
    font-size: 72px;
    line-height: 0.6;
    margin-bottom: 10px;
    background: linear-gradient(90deg, #ffcc00, $pop-accent);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  &__quote {
    font-family: 'Mulish', sans-serif;
    font-size: clamp(17px, 2.3vh, 21px);
    line-height: 1.4;
    font-weight: 600;
    color: #fff;
    margin: 0 0 26px;
    min-height: clamp(0px, 16vh, 150px);
    letter-spacing: -0.015em;
  }

  &__author {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: left;
  }

  &__avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ffe066, #ffcc00);
    color: $pop-teal;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 14px;
  }

  &__name { font-weight: 700; font-size: 14.5px; color: #fff; }
  &__role { font-size: 13px; color: rgba(255, 255, 255, 0.7); }

  &__dots { display: flex; gap: 8px; margin-top: clamp(12px, 2.9vh, 26px); }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    border: 0;
    padding: 0;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    transition: width 0.2s;

    &.is-active { width: 24px; background: linear-gradient(90deg, #ffcc00, $pop-accent); }
  }

  &__chips {
    position: relative;
    display: flex;
    gap: 10px;
    margin-top: clamp(14px, 3.3vh, 30px);
    flex-wrap: wrap;
    justify-content: center;
  }

  &__chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    backdrop-filter: blur(8px);
  }
}

.auth-aside-fade-enter-active, .auth-aside-fade-leave-active { transition: opacity 0.45s ease, transform 0.45s ease; }
.auth-aside-fade-enter { opacity: 0; transform: translateY(6px); }
.auth-aside-fade-leave-to { opacity: 0; }
</style>
